import { Controller } from 'stimulus';
import { t } from '../javascript/lib/i18n';

const LOCAL_STORAGE_KEY_PREFIX = 'rpm-session-time';

export default class extends Controller {
  static targets = ['confirmationLink'];

  static values = {
    formSubmitted: Boolean,
    completed: Boolean,
    vipId: Number,
    userId: Number,
  };

  connect() {
    if (this.completedValue) {
      return;
    }

    this.isInternalNavigation = false;
    this.leaveConfirmed = false;

    // Any internal link clicks will be intercepted by this event listener
    document.addEventListener(
      'turbo:before-visit',
      this.handleBeforeTurboVisit
    );

    // The confirmation modal renders its own copy of this form, so a second
    // instance of this controller is live while the modal is open — and that
    // one never saw the `turbo:before-visit` that opened the modal, so its
    // `isInternalNavigation` is false. Record the confirmation at click time
    // instead: it is the only moment both instances can agree on, and it
    // doesn't depend on where focus happens to be when `beforeunload` runs.
    document.addEventListener('click', this.handleSkipConfirmationClick, true);

    // Any external navigation will be intercepted by this event listenter
    // and warn the user if they have unsaved changes i.e. closing the tab,
    // hard refresh, etc.
    window.addEventListener('beforeunload', this.handleBeforeUnload);

    // This event listener will listen for a successful form submission
    // and will dispatch an event that is defined in the rpm_session_timer_controller.js
    // to reset the timer in the localStorage. This must be done after the form is submitted
    // to ensure the elapsed time is not lost.
    this.element.addEventListener(
      'turbo:submit-success',
      () => {
        this.element.dispatchEvent(
          new CustomEvent('rpmTimeSubmitted', {
            bubbles: true,
          })
        );
      },
      { once: true }
    );
  }

  handleBeforeTurboVisit = (event) => {
    // `document.activeElement` cannot identify the clicked link on its own: focus
    // may sit elsewhere (an autofocused modal input) and Safari never focuses a
    // clicked anchor. So the capture-phase handler's flag comes first, with
    // activeElement as the fallback for navigations that had no click.
    const sourceElement = document.activeElement;
    const skipRpmConfirmation =
      this.leaveConfirmed ||
      sourceElement.hasAttribute('data-skip-rpm-confirmation');
    const url = new URL(event.detail.url);
    const override = url.searchParams.get('force_leave') === 'true';

    if (!this.formSubmittedValue && !override && !skipRpmConfirmation) {
      this.isInternalNavigation = true;
      event.preventDefault();

      // Clicking the confirmation link target triggers the exit popup
      // when there are unsaved changes and confirms the user wants to leave
      // without logging rpm time

      const link = this.confirmationLinkTarget;
      const originalHref = new URL(link.href);

      originalHref.searchParams.set('destination_url', event.detail.url);
      link.href = originalHref.toString();
      link.click();
    }
  };

  // `data-skip-rpm-confirmation` means the user has already answered this
  // question (the modal's "Leave anyway" link). Assigned on every click, never
  // latched: a click on a skip-marked control that does not navigate would
  // otherwise suppress the confirmation for an unrelated navigation later.
  handleSkipConfirmationClick = (event) => {
    this.leaveConfirmed = !!event.target.closest?.(
      '[data-skip-rpm-confirmation]'
    );
  };

  handleBeforeUnload = (event) => {
    // Sometimes this fires after a turbo click, so if we're navigating internally
    // we don't want to show the warning as we'll handle it in the `turbo:before-visit` event.
    // Hence the use of `isInternalNavigation` to prevent the warning from showing.
    const sourceElement = document.activeElement;
    const skipRpmConfirmation =
      this.leaveConfirmed ||
      sourceElement.hasAttribute('data-skip-rpm-confirmation');

    if (
      !this.formSubmittedValue &&
      !this.isInternalNavigation &&
      !skipRpmConfirmation
    ) {
      event.preventDefault();
      event.returnValue = t('js.rpm_confirm_navigation.unsaved_changes');
      return event.returnValue;
    }
    this.isInternalNavigation = false;
  };

  disconnect() {
    document.removeEventListener(
      'turbo:before-visit',
      this.handleBeforeTurboVisit
    );
    document.removeEventListener(
      'click',
      this.handleSkipConfirmationClick,
      true
    );
    window.removeEventListener('beforeunload', this.handleBeforeUnload);
  }

  submitRpmTime() {
    // This method is called when an RPM Assessment form is submitted and
    // it will populate the hidden rpm_time input with the current elapsed time.
    // This is the first step in the process of saving the RPM Assessment form.
    // The second step is defined in the "connect" method above, where an event
    // listener is added to listen for a successful form submission.
    const rpmTimeInputs = document.querySelectorAll(
      '#form_objects_rpm_assessment_form_rpm_time'
    );
    const rpmStorageObject = JSON.parse(localStorage.getItem(this.storageKey));

    if (rpmStorageObject && rpmStorageObject.time !== undefined) {
      rpmTimeInputs.forEach((input) => {
        input.value = rpmStorageObject.time;
      });
    } else {
      console.warn(
        'RPM storage object is null or missing the "time" property.'
      );
    }

    this.formSubmittedValue = true;
  }

  get storageKey() {
    return `${LOCAL_STORAGE_KEY_PREFIX}-${this.userIdValue}-${this.vipIdValue}`;
  }
}
